(() => {
  window.__perfumiarzFavoritesBridgeLoaded = true;
  const STORAGE_KEY = 'perfumiarz:favorites:v1';
  const CHANGE_EVENT = 'perfumiarz:favorites:change';
  const FROONZE_READY_EVENT = 'frcp.wishlist.installed';
  const FROONZE_UPDATE_EVENT = 'frcp.wishlist.update';
  const FROONZE_INITIALIZE_EVENT = 'frcp.wishlist.initialize';
  const FROONZE_BUTTON_SELECTOR = '.frcp-wishlist-btn:not([data-favorite-toggle])';
  const PENDING_STATE_MS = 1800;
  const FROONZE_READY_ATTEMPTS = 40;

  const normalizeHandle = (value) => (value || '').toString().trim().toLowerCase();
  const parseId = (value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const normalizeList = (list) =>
    [...new Set((Array.isArray(list) ? list : []).map(normalizeHandle).filter((item) => item.length > 0))];

  const areSameFavorites = (first, second) => {
    const normalizedFirst = normalizeList(first);
    const normalizedSecond = normalizeList(second);
    return normalizedFirst.length === normalizedSecond.length && normalizedFirst.every((value, index) => value === normalizedSecond[index]);
  };

  const readFavorites = () => {
    if (isFroonzeWishlistReady()) {
      return syncFavoritesFromFroonze();
    }

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      return normalizeList(JSON.parse(raw));
    } catch (_error) {
      return [];
    }
  };

  const dispatchFavoritesChange = (favorites) => {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { favorites } }));
  };

  const writeFavorites = (favorites, options = {}) => {
    const normalized = normalizeList(favorites);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch (_error) {
      return normalized;
    }
    if (options.dispatch !== false) dispatchFavoritesChange(normalized);
    return normalized;
  };

  const isFavorite = (handle, favorites = readFavorites()) => favorites.includes(normalizeHandle(handle));

  const getShopifyRoot = () => window.Shopify?.routes?.root || '/';

  const getShopifyLocale = () => window.Shopify?.locale || document.documentElement.lang || 'en';

  const getFroonzeWishlistApi = () => {
    if (!window.frcp || !window.frcp.wishlist || !window.frcp.wishlist.initialized) return null;
    return window.frcp.wishlist;
  };

  const isFroonzeWishlistReady = () => {
    const wishlist = getFroonzeWishlistApi();
    return Boolean(wishlist && typeof wishlist.inWishlist === 'function');
  };

  const getFroonzeActiveListName = () => {
    const wishlist = getFroonzeWishlistApi();
    return wishlist?.data?.activeList || 'default';
  };

  const getFroonzeFavorites = () => {
    const wishlist = getFroonzeWishlistApi();
    if (!wishlist || !wishlist.data) return [];

    const activeList = getFroonzeActiveListName();
    const baseList = Object.keys(wishlist.data.lists?.[activeList] || {});
    const guestAdds = Object.keys(wishlist.data.guest?.add || {});
    const guestRemoves = new Set(Object.keys(wishlist.data.guest?.remove || {}));

    return normalizeList([...baseList, ...guestAdds].filter((handle) => !guestRemoves.has(handle)));
  };

  const syncFavoritesFromFroonze = () => {
    const favorites = getFroonzeFavorites();
    const storedFavorites = (() => {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? normalizeList(JSON.parse(raw)) : [];
      } catch (_error) {
        return [];
      }
    })();

    if (!areSameFavorites(favorites, storedFavorites)) {
      writeFavorites(favorites);
      return favorites;
    }

    return favorites;
  };

  const isFroonzeFavorite = (button) => {
    const wishlist = getFroonzeWishlistApi();
    if (!wishlist || typeof wishlist.inWishlist !== 'function') return false;

    const handle = button.getAttribute('data-product-handle');
    const productId = parseId(button.getAttribute('data-product-id'));
    const variantId = parseId(getCurrentVariantId(button));
    return Boolean(wishlist.inWishlist(handle, productId, variantId));
  };

  const hasCurrentPendingState = (button) => {
    const pendingUntil = Number.parseInt(button.dataset.favoritePendingUntil || '0', 10);
    return pendingUntil > Date.now();
  };

  const clearPendingState = (button) => {
    delete button.dataset.favoritePendingUntil;
    delete button.dataset.favoritePendingState;
  };

  const matchesFavoriteButton = (source, target) => {
    const sourceHandle = source.getAttribute('data-product-handle');
    const sourceProductId = source.getAttribute('data-product-id');
    const sourceVariantId = String(getCurrentVariantId(source) || '');
    const targetHandle = target.getAttribute('data-product-handle');
    const targetProductId = target.getAttribute('data-product-id');
    const targetVariantId = String(getCurrentVariantId(target) || '');

    if (sourceProductId && targetProductId && sourceProductId === targetProductId) return true;
    if (sourceVariantId && targetVariantId && sourceVariantId === targetVariantId) return true;
    return Boolean(sourceHandle && targetHandle && sourceHandle === targetHandle);
  };

  const setPendingStateForMatches = (sourceButton, active) => {
    const pendingUntil = String(Date.now() + PENDING_STATE_MS);

    document.querySelectorAll('[data-favorite-toggle]').forEach((button) => {
      if (!matchesFavoriteButton(sourceButton, button)) return;
      button.dataset.favoritePendingUntil = pendingUntil;
      button.dataset.favoritePendingState = String(active);
      setButtonState(button, active);
    });
  };

  const getCurrentVariantId = (button) => {
    const scopedForm = button.closest('form[action*="/cart/add"]');
    const scopedVariant = scopedForm?.querySelector('[name="id"]')?.value;
    if (scopedVariant) return scopedVariant;

    const productScope = button.closest('product-info, quick-add-modal, .product, .product-figma-buy-buttons');
    const productVariant = productScope?.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
    if (productVariant) return productVariant;

    return button.getAttribute('data-product-variant-id');
  };

  const getNativeFroonzeButton = (button) => {
    const localWrapper = button.parentElement?.querySelector('[data-froonze-native-wrapper]');
    const localButton = localWrapper?.querySelector(FROONZE_BUTTON_SELECTOR);
    if (localButton) return localButton;

    const handle = button.getAttribute('data-product-handle');
    if (!handle) return null;

    return document.querySelector(
      `[data-froonze-native-wrapper][data-frcp-product-handle="${CSS.escape(handle)}"] ${FROONZE_BUTTON_SELECTOR}`
    );
  };

  const clearCustomFroonzeClasses = (button) => {
    button.classList.remove('frcp-wishlist-btn', 'frcp-wishlist-btn--add', 'frcp-wishlist-btn--remove', 'frcp-wishlist-btn--loading');
    delete button.dataset.frcpInWishlist;
    delete button.dataset.frcpProductHandle;
    delete button.dataset.frcpProductId;
    delete button.dataset.frcpVariantId;
    delete button.dataset.frcpProductTitle;
  };

  const syncFroonzePlaceholderMode = () => {
    if (!window.frcp?.wishlist?.blockSettings) return;
    window.frcp.wishlist.blockSettings.collections_wishlist_button = 'placeholder';
  };

  const attachFroonzePlaceholders = () => {
    if (!isFroonzeWishlistReady()) return;
    syncFroonzePlaceholderMode();
    if (typeof window.frcp.wishlist.attachOnCollection === 'function') {
      window.frcp.wishlist.attachOnCollection();
    }
  };

  const createFroonzeBridgeButton = (button) => {
    const bridge = document.createElement('span');
    bridge.hidden = true;
    bridge.className = 'perfumiarz-favorite-proxy frcp-wishlist-btn';
    bridge.dataset.frcpInWishlist = String(isFroonzeFavorite(button));

    const handle = button.getAttribute('data-product-handle');
    const productId = button.getAttribute('data-product-id');
    const variantId = getCurrentVariantId(button);
    const title = button.getAttribute('data-product-title');

    if (handle) bridge.dataset.frcpProductHandle = handle;
    if (productId) bridge.dataset.frcpProductId = productId;
    if (variantId) bridge.dataset.frcpVariantId = variantId;
    if (title) bridge.dataset.frcpProductTitle = title;

    document.body.append(bridge);
    return bridge;
  };

  const submitFavoriteThroughFroonze = (button) => {
    const wishlist = getFroonzeWishlistApi();
    if (!wishlist) return false;

    attachFroonzePlaceholders();
    const nativeButton = getNativeFroonzeButton(button);
    if (nativeButton) {
      nativeButton.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      return true;
    }

    const bridge = createFroonzeBridgeButton(button);
    const fakeEvent = {
      target: bridge,
      preventDefault: () => {},
      stopPropagation: () => {},
    };

    if (typeof wishlist.submitWishlist === 'function') {
      wishlist.submitWishlist(fakeEvent);
    } else {
      bridge.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    }

    window.setTimeout(() => bridge.remove(), 5000);
    return true;
  };

  const setButtonState = (button, active) => {
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));

    const addLabel = button.getAttribute('data-fav-label-add') || '';
    const removeLabel = button.getAttribute('data-fav-label-remove') || addLabel;
    if (addLabel || removeLabel) button.setAttribute('aria-label', active ? removeLabel : addLabel);
  };

  const markPendingFroonzeToggle = (button) => {
    const nextActiveState = !isFroonzeFavorite(button);

    setPendingStateForMatches(button, nextActiveState);
    window.setTimeout(syncUiFromFroonze, 900);
    window.setTimeout(syncUiFromFroonze, 2200);
  };

  const toggleFavorite = (handle) => {
    const normalizedHandle = normalizeHandle(handle);
    if (!normalizedHandle) return readFavorites();

    const favorites = readFavorites();
    const exists = favorites.includes(normalizedHandle);
    const nextFavorites = exists
      ? favorites.filter((item) => item !== normalizedHandle)
      : [...favorites, normalizedHandle];
    return writeFavorites(nextFavorites);
  };

  const updateFavoriteToggleStates = (favorites = readFavorites()) => {
    document.querySelectorAll('[data-favorite-toggle]').forEach((button) => {
      if (hasCurrentPendingState(button)) return;
      clearPendingState(button);

      const handle = button.getAttribute('data-product-handle');
      const active = isFroonzeWishlistReady() ? isFroonzeFavorite(button) : isFavorite(handle, favorites);

      setButtonState(button, active);
      if (isFroonzeWishlistReady()) clearCustomFroonzeClasses(button);
    });

    document.querySelectorAll('[data-favorites-count]').forEach((node) => {
      node.textContent = String(favorites.length);
    });

    updateHeaderWishlist(favorites.length);
  };

  const getFroonzeUpdatePayload = (event) => event?.detail?.data || event?.detail || null;

  const updateButtonsFromFroonzePayload = (event) => {
    const payload = getFroonzeUpdatePayload(event);
    if (!payload || typeof payload.inWishlist !== 'boolean') return;

    const productId = payload.productId ? String(payload.productId) : '';
    const variantIds = Array.isArray(payload.variantExternalIds)
      ? payload.variantExternalIds.map((id) => String(id))
      : [];

    document.querySelectorAll('[data-favorite-toggle]').forEach((button) => {
      const buttonProductId = button.getAttribute('data-product-id');
      const buttonVariantId = String(getCurrentVariantId(button) || '');
      const productMatches = productId && buttonProductId === productId;
      const variantMatches = variantIds.length > 0 && variantIds.includes(buttonVariantId);

      if (productMatches || variantMatches) {
        clearPendingState(button);
        setButtonState(button, payload.inWishlist);
      }
    });
  };

  const syncUiFromFroonzeSoon = (event) => {
    updateButtonsFromFroonzePayload(event);
    syncUiFromFroonze();
    window.setTimeout(syncUiFromFroonze, 250);
    window.setTimeout(syncUiFromFroonze, 1000);
  };

  const syncUiFromFroonze = () => {
    if (!isFroonzeWishlistReady()) return;
    attachFroonzePlaceholders();
    const favorites = syncFavoritesFromFroonze();
    updateFavoriteToggleStates(favorites);
  };

  const refreshFavoriteUi = () => {
    updateFavoriteToggleStates(readFavorites());
  };

  let refreshQueued = false;
  const queueFavoriteUiRefresh = () => {
    if (refreshQueued) return;
    refreshQueued = true;
    window.setTimeout(() => {
      refreshQueued = false;
      refreshFavoriteUi();
    }, 50);
  };

  const waitForFroonzeThenToggle = (button, attempts = FROONZE_READY_ATTEMPTS) => {
    if (isFroonzeWishlistReady()) {
      submitFavoriteThroughFroonze(button);
      return;
    }

    if (attempts <= 0) return;
    window.setTimeout(() => waitForFroonzeThenToggle(button, attempts - 1), 150);
  };

  const getFroonzeWishlistCount = (fallbackCount = 0) => {
    const wishlist = getFroonzeWishlistApi();
    if (wishlist && typeof wishlist.wishlistCount === 'function') {
      const count = Number.parseInt(wishlist.wishlistCount(), 10);
      return Number.isNaN(count) ? fallbackCount : Math.max(count, 0);
    }

    return fallbackCount;
  };

  const getFroonzeWishlistUrl = () => {
    const root = getShopifyRoot();
    const wishlist = getFroonzeWishlistApi();
    const customerPage = window.frcp?.customerPage;
    const shopId = window.frcp?.shopId;
    const customerAccountPageId = wishlist?.pageHandle || customerPage?.accountPage;

    if (wishlist?.settings?.customer_account_version !== 'new_customer_accounts') {
      return `${root}account#/wishlist`;
    }

    if (shopId && customerAccountPageId) {
      return `https://shopify.com/${shopId}/account/pages/${customerAccountPageId}?locale=${getShopifyLocale()}`;
    }

    if (shopId) {
      return `https://shopify.com/${shopId}/account/profile?locale=${getShopifyLocale()}`;
    }

    return `${root}account`;
  };

  const ensureHeaderCountBadge = (link) => {
    let badge = link.querySelector('[data-favorites-count]');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'header__favorites-count cart-count-bubble';
      badge.setAttribute('data-favorites-count', '');
      badge.setAttribute('aria-hidden', 'true');
      link.append(badge);
    }

    let badgeValue = badge.querySelector('[data-favorites-count-value]');
    if (!badgeValue) {
      const currentValue = badge.textContent.trim() || '0';
      badge.textContent = '';
      badgeValue = document.createElement('span');
      badgeValue.setAttribute('data-favorites-count-value', '');
      badgeValue.textContent = currentValue;
      badge.append(badgeValue);
    }

    return badge;
  };

  const updateHeaderWishlist = (fallbackCount = readFavorites().length) => {
    const count = getFroonzeWishlistCount(fallbackCount);
    const froonzeFallbackHref = `${getShopifyRoot()}account#/wishlist`;
    const href = isFroonzeWishlistReady()
      ? getFroonzeWishlistUrl()
      : window.frcp?.plugins?.wishlist
        ? froonzeFallbackHref
        : '/pages/favorites';

    document.querySelectorAll('.header__icon--favorites, [data-header-favorites-link]').forEach((link) => {
      link.setAttribute('href', href);
      const badge = ensureHeaderCountBadge(link);
      const badgeValue = badge.querySelector('[data-favorites-count-value]');
      if (badgeValue) badgeValue.textContent = String(count);
      badge.hidden = count <= 0;
    });

    document.querySelectorAll('.hn-hover-panel__action-secondary[href]').forEach((link) => {
      const label = (link.getAttribute('aria-label') || '').toLowerCase();
      if (label.includes('favorite') || label.includes('wishlist')) link.setAttribute('href', href);
    });
  };

  const formatMoney = (priceCents, locale, currencyCode) => {
    const parsed = Number(priceCents);
    if (Number.isNaN(parsed)) return '';

    try {
      return new Intl.NumberFormat(locale || 'pl-PL', {
        style: 'currency',
        currency: currencyCode || 'PLN',
      }).format(parsed / 100);
    } catch (_error) {
      return `${(parsed / 100).toFixed(2)} ${currencyCode || 'PLN'}`;
    }
  };

  const escapeHtml = (value) =>
    (value || '')
      .toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const renderFavoriteCardMarkup = (product, options = {}) => {
    const image = product?.images?.[0];
    const firstVariantId = product?.variants?.[0]?.id;
    const locale = options.locale || 'pl-PL';
    const currency = options.currency || 'PLN';
    const priceSuffix = options.priceSuffix || '';
    const viewProductLabel = options.viewProductLabel || '';
    const favoriteAddLabel = options.favoriteAddLabel || '';
    const favoriteRemoveLabel = options.favoriteRemoveLabel || favoriteAddLabel;
    const backInStockEnabled = Boolean(options.backInStockEnabled);
    const notifyLabel = options.notifyLabel || '';
    const outOfStockLabel = options.outOfStockLabel || '';
    const isAvailable = Boolean(product?.available);
    const productUrl = `/products/${encodeURIComponent(product.handle)}`;
    const price = formatMoney(product?.price, locale, currency);
    const description = (product?.description || '').replace(/<[^>]*>?/gm, '').trim().slice(0, 120);
    const availabilityAction = isAvailable
      ? `<a class="perfumiarz-favorites__product-link" href="${productUrl}">${escapeHtml(viewProductLabel)}</a>`
      : backInStockEnabled
        ? `<a class="perfumiarz-favorites__product-link perfumiarz-favorites__product-link--notify" href="${productUrl}#perfumiarz-notify">${escapeHtml(notifyLabel)}</a>`
        : `<button type="button" class="perfumiarz-favorites__product-link perfumiarz-favorites__product-link--out-of-stock" disabled>${escapeHtml(outOfStockLabel)}</button>`;

    return `
      <article class="perfumiarz-favorites__card${isAvailable ? '' : ' perfumiarz-favorites__card--sold-out'}" data-favorite-card="${escapeHtml(product.handle)}">
        ${isAvailable ? '' : `<span class="perfumiarz-favorites__stock-badge">${escapeHtml(outOfStockLabel)}</span>`}
        <a class="perfumiarz-favorites__image-link" href="${productUrl}" aria-label="${escapeHtml(product.title)}">
          ${
            image
              ? `<img class="perfumiarz-favorites__image" src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">`
              : `<span class="perfumiarz-favorites__image-placeholder"></span>`
          }
        </a>
        <div class="perfumiarz-favorites__content">
          <h3 class="perfumiarz-favorites__title">
            <a href="${productUrl}">${escapeHtml(product.title)}</a>
          </h3>
          ${description ? `<p class="perfumiarz-favorites__description">${escapeHtml(description)}</p>` : ''}
          <p class="perfumiarz-favorites__price">
            ${escapeHtml(price)}${priceSuffix ? `<span class="perfumiarz-favorites__price-suffix"> ${escapeHtml(priceSuffix)}</span>` : ''}
          </p>
        </div>
        <div class="perfumiarz-favorites__actions">
          ${availabilityAction}
          <button
            type="button"
            class="perfumiarz-favorite-button is-active"
            data-favorite-toggle
            data-product-handle="${escapeHtml(product.handle)}"
            data-product-id="${escapeHtml(product.id)}"
            data-product-variant-id="${escapeHtml(firstVariantId)}"
            data-product-title="${escapeHtml(product.title)}"
            data-fav-label-add="${escapeHtml(favoriteAddLabel)}"
            data-fav-label-remove="${escapeHtml(favoriteRemoveLabel)}"
            aria-pressed="true"
            aria-label="${escapeHtml(favoriteRemoveLabel || favoriteAddLabel)}"
          >
            <span class="perfumiarz-favorite-button__icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 13" fill="none">
            <path d="M12.4395 1.4306C13.0056 1.99722 13.3321 2.75993 13.3512 3.56068C13.3703 4.36143 13.0806 5.13885 12.5422 5.73185L6.93322 11.9015L1.32653 5.73185C1.04195 5.42349 0.822553 5.06085 0.681477 4.66567C0.540402 4.27048 0.480555 3.85089 0.505525 3.43202C0.530494 3.01316 0.639765 2.60364 0.826789 2.22802C1.01381 1.85239 1.27474 1.51839 1.59394 1.24602C1.91314 0.973648 2.28404 0.76852 2.68441 0.642925C3.08478 0.51733 3.50639 0.473852 3.92397 0.515097C4.34155 0.556342 4.7465 0.681461 5.11456 0.882957C5.48263 1.08445 5.80623 1.35818 6.06598 1.68773L6.93399 2.77179L7.80123 1.68773C8.07526 1.34564 8.4183 1.06514 8.808 0.864531C9.1977 0.663918 9.62531 0.547692 10.0629 0.523431C10.5006 0.499169 10.9384 0.567418 11.3479 0.723726C11.7574 0.880034 12.1293 1.12089 12.4395 1.4306Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </button>
        </div>
      </article>
    `;
  };

  const renderFavoritesPage = async (container) => {
    const grid = container.querySelector('[data-favorites-grid]');
    const emptyState = container.querySelector('[data-favorites-empty]');
    if (!grid) return;

    const favorites = readFavorites();
    updateFavoriteToggleStates(favorites);

    if (!favorites.length) {
      grid.innerHTML = '';
      if (emptyState) emptyState.hidden = false;
      return;
    }

    if (emptyState) emptyState.hidden = true;
    grid.setAttribute('aria-busy', 'true');

    const locale = container.getAttribute('data-locale') || 'pl-PL';
    const currency = container.getAttribute('data-currency') || 'PLN';
    const priceSuffix = container.getAttribute('data-price-suffix') || '';
    const favoriteAddLabel = container.getAttribute('data-favorite-add-label') || '';
    const favoriteRemoveLabel = container.getAttribute('data-favorite-remove-label') || favoriteAddLabel;
    const viewProductLabel = container.getAttribute('data-view-product-label') || '';
    const backInStockEnabled = container.getAttribute('data-back-in-stock-enabled') === 'true';
    const notifyLabel = container.getAttribute('data-notify-label') || '';
    const outOfStockLabel = container.getAttribute('data-out-of-stock-label') || '';

    const productRequests = favorites.map((handle) =>
      fetch(`/products/${encodeURIComponent(handle)}.js`)
        .then((response) => {
          if (!response.ok) return null;
          return response.json();
        })
        .catch(() => null)
    );

    const products = (await Promise.all(productRequests)).filter(Boolean);
    const orderByHandle = new Map(favorites.map((handle, index) => [handle, index]));
    products.sort((a, b) => (orderByHandle.get(a.handle) || 0) - (orderByHandle.get(b.handle) || 0));

    if (!products.length) {
      grid.innerHTML = '';
      if (emptyState) emptyState.hidden = false;
      writeFavorites([]);
      grid.removeAttribute('aria-busy');
      return;
    }

    grid.innerHTML = products
      .map((product) =>
        renderFavoriteCardMarkup(product, {
          locale,
          currency,
          priceSuffix,
          favoriteAddLabel,
          favoriteRemoveLabel,
          viewProductLabel,
          backInStockEnabled,
          notifyLabel,
          outOfStockLabel,
        })
      )
      .join('');
    grid.removeAttribute('aria-busy');
    updateFavoriteToggleStates(readFavorites());
  };

  const handleFavoriteToggleClick = (event) => {
    const button = event.target.closest('[data-favorite-toggle]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    if (isFroonzeWishlistReady()) {
      markPendingFroonzeToggle(button);
      submitFavoriteThroughFroonze(button);
      return;
    }

    if (window.frcp?.plugins?.wishlist) {
      event.preventDefault();
      waitForFroonzeThenToggle(button);
      return;
    }

    const handle = button.getAttribute('data-product-handle');
    const favorites = toggleFavorite(handle);
    updateFavoriteToggleStates(favorites);
  };

  const bootstrap = () => {
    document.addEventListener('click', handleFavoriteToggleClick);
    refreshFavoriteUi();

    document.addEventListener(FROONZE_READY_EVENT, syncUiFromFroonzeSoon);
    document.addEventListener(FROONZE_INITIALIZE_EVENT, syncUiFromFroonzeSoon);
    document.addEventListener(FROONZE_UPDATE_EVENT, syncUiFromFroonzeSoon);
    document.addEventListener('theme:loading:end', refreshFavoriteUi);
    document.addEventListener('shopify:section:load', refreshFavoriteUi);
    document.addEventListener('quick-add-modal:open', refreshFavoriteUi);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) syncUiFromFroonzeSoon();
    });
    window.addEventListener('focus', () => syncUiFromFroonzeSoon());
    window.addEventListener('pageshow', () => syncUiFromFroonzeSoon());

    new MutationObserver((mutations) => {
      const hasNewFavoriteTarget = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (
              node.matches?.('[data-favorite-toggle], [data-froonze-native-wrapper]') ||
              node.querySelector?.('[data-favorite-toggle], [data-froonze-native-wrapper]')
            )
        )
      );

      if (hasNewFavoriteTarget) {
        if (isFroonzeWishlistReady()) attachFroonzePlaceholders();
        queueFavoriteUiRefresh();
      }
    }).observe(document.body, { childList: true, subtree: true });

    const favoritesContainer = document.querySelector('[data-favorites-page]');
    if (favoritesContainer) {
      renderFavoritesPage(favoritesContainer);
      document.addEventListener(CHANGE_EVENT, () => renderFavoritesPage(favoritesContainer));
    }

    if (isFroonzeWishlistReady()) {
      attachFroonzePlaceholders();
      syncUiFromFroonze();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
